import { axios } from 'api'
import { Field, FieldArray, Form, Formik } from 'formik'
import React, { useEffect, useState } from 'react'
import Collapse from 'ui/bend/animations/Collapse'
import * as Yup from 'yup'
import BaseButton from '../Components/Common/Button'
import {
  FieldWrapper,
  Label,
  Main,
  SectionHeader,
  SectionWrapper
} from '../ContentStructure/Components'
import SubmitButtonComponent from '../ContentStructure/SubmitButton'
import BaseInputText from '../Components/Common/TextInput'
import { LeftChevron } from 'images'
import CadenceMatrix, { defaultCadenceMaps, repairCadenceMaps } from './CadenceMatrix'
import CoachingDayItem from './CoachingDayItem'

const emptyDay = () => ({
  type: 'class_from_another_series',
  id: '',
  series: '',
  itemTag: '',
  description: ''
})

const SLOTS_PER_WEEK = 7
const MIN_CLASSES_PER_WEEK = 5

const emptyWeek = (index) => ({
  title: `Week ${index + 1}`,
  description: '',
  items: Array.from({ length: SLOTS_PER_WEEK }, emptyDay),
  cadenceMaps: { ...defaultCadenceMaps }
})

const validationSchema = Yup.object({
  sections: Yup.array().of(
    Yup.object({
      title: Yup.string(),
      description: Yup.string().required('Week description is required'),
      items: Yup.array()
        .of(
          Yup.object({
            id: Yup.string(),
            series: Yup.string()
          })
        )
        .length(SLOTS_PER_WEEK)
        .test('classes-selected', function validateClasses(items = []) {
          const { path, createError } = this
          const nested = []
          items.forEach((item, index) => {
            const series = item?.series
            const classId = item?.id
            if (index < MIN_CLASSES_PER_WEEK) {
              if (!series) {
                nested.push(
                  createError({
                    path: `${path}[${index}].series`,
                    message: 'Series is required'
                  })
                )
              }
              if (!classId) {
                nested.push(
                  createError({
                    path: `${path}[${index}].id`,
                    message: 'Class is required'
                  })
                )
              }
            } else if (series && !classId) {
              nested.push(
                createError({
                  path: `${path}[${index}].id`,
                  message: 'Class is required'
                })
              )
            }
          })
          if (nested.length) return new Yup.ValidationError(nested)
          return true
        })
    })
  ),
})

const PersonalPlanStructure = ({ programId, seriesItems = [] }) => {
  const [initialValues, setInitialValues] = useState(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [success, setSuccess] = useState(false)
  const [classMap, setClassMap] = useState({})
  const [openSections, setOpenSections] = useState({ 0: true })
  const [serverError, setServerError] = useState('')

  const getClassListbyPlan = async (planId) => {
    if (!planId || classMap[`plan_${planId}`]) return
    try {
      const res = await axios.post(
        'plans/get_classes',
        { plan_id: planId },
        { baseURL: '/admin/api/' }
      )
      const data = res.data?.classes[planId] || []
      setClassMap((prevState) => ({
        ...prevState,
        [`plan_${planId}`]: data
      }))
    } catch (err) {
      console.error(err)
    }
  }

  useEffect(() => {
    const load = async () => {
      try {
        const res = await axios.get(`personal_plans/${programId}`, {
          baseURL: '/admin/api/'
        })
        const weeks = (res.data?.weeks || [emptyWeek(0)]).map((week, weekIndex) => {
          const rawCadence =
            week.cadence_maps ||
            week.cadenceMaps ||
            res.data?.cadence_maps?.[weekIndex + 1] ||
            res.data?.cadence_maps?.[String(weekIndex + 1)] ||
            {}
          const items = Array.from({ length: SLOTS_PER_WEEK }, (_, index) => ({
            ...emptyDay(),
            ...(week.items?.[index] || {})
          }))
          return {
            ...week,
            items,
            cadenceMaps: repairCadenceMaps(items, {
              ...defaultCadenceMaps,
              ...rawCadence
            })
          }
        })
        setInitialValues({ sections: weeks })

        const seriesIds = weeks.flatMap((week) =>
          week.items.map((item) => item.series).filter(Boolean)
        )
        if (seriesIds.length > 0) {
          const classRes = await axios.post(
            'plans/get_classes',
            { plan_id: seriesIds },
            { baseURL: '/admin/api/' }
          )
          const classData = classRes.data?.classes || {}
          setClassMap(
            seriesIds.reduce(
              (acc, id) => ({
                ...acc,
                [`plan_${id}`]: classData[id] || []
              }),
              {}
            )
          )
        }
      } catch (err) {
        console.error(err)
        setInitialValues({
          sections: [emptyWeek(0)]
        })
      }
    }
    load()
  }, [programId])

  const handleSubmit = (values) => {
    setIsSubmitting(true)
    setSuccess(false)
    setServerError('')
    axios
      .post(
        `personal_plans/${programId}/update_structure`,
        {
          weeks: values.sections.map(({ items, cadenceMaps, ...week }) => ({
            ...week,
            items: items.map((item) => ({
              type: 'class',
              id: item.id || '',
              series: item.series || '',
              item_tag: item.itemTag || '',
              description: item.description || ''
            })),
            cadence_maps: cadenceMaps
          })),
          cadence_maps: values.sections.reduce((acc, week, index) => {
            acc[index + 1] = week.cadenceMaps
            return acc
          }, {})
        },
        { baseURL: '/admin/api/' }
      )
      .then((res) => {
        if (res.data?.status === 'ok') {
          setSuccess(true)
        } else {
          setServerError(res.data?.error || 'Could not save')
        }
        setIsSubmitting(false)
      })
      .catch((err) => {
        setServerError(err.response?.data?.error || 'Could not save')
        setIsSubmitting(false)
      })
  }

  if (!initialValues) return null

  return (
    <Main>
      <Formik
        initialValues={initialValues}
        onSubmit={handleSubmit}
        validationSchema={validationSchema}
        enableReinitialize
      >
        {({ values, setFieldValue, errors, touched }) => (
          <Form>
            <FieldArray name='sections'>
              {({ push, remove }) => (
                <>
                  {values.sections.map((week, sectionIndex) => {
                    const isOpen = Boolean(openSections[sectionIndex])
                    const filledClasses = week.items.filter((item) => item.id).length
                    const classMeta =
                      filledClasses >= MIN_CLASSES_PER_WEEK
                        ? `${filledClasses} classes`
                        : `${filledClasses} of ${MIN_CLASSES_PER_WEEK} classes`
                    return (
                      <SectionWrapper key={sectionIndex}>
                        <div className='flex-box'>
                          <div className='left'>
                            <SectionHeader
                              onClick={(e) => {
                                e.preventDefault()
                                setOpenSections((prev) => ({
                                  ...prev,
                                  [sectionIndex]: !prev[sectionIndex]
                                }))
                              }}
                            >
                              Week {sectionIndex + 1}
                              <span className='section-meta'>
                                {classMeta}
                                {!isOpen && week.title ? ` · ${week.title}` : ''}
                              </span>
                            </SectionHeader>
                          </div>
                          <div className='right'>
                            {values.sections.length > 1 && (
                              <BaseButton
                                className='remove-section-btn'
                                onClick={() => remove(sectionIndex)}
                              >
                                Remove week
                              </BaseButton>
                            )}
                            <BaseButton
                              onClick={(e) => {
                                e.preventDefault()
                                setOpenSections((prev) => ({
                                  ...prev,
                                  [sectionIndex]: !prev[sectionIndex]
                                }))
                              }}
                              className={`collapse-button ${
                                isOpen ? 'opened' : 'closed'
                              }`}
                            >
                              <LeftChevron />
                            </BaseButton>
                          </div>
                        </div>
                        <Collapse isOpened={isOpen}>
                          <div className='align-items-center title-row'>
                            <BaseInputText
                              name={`sections[${sectionIndex}].title`}
                              placeholder={`Week ${sectionIndex + 1}`}
                              label={`Group name (default: Week ${
                                sectionIndex + 1
                              })`}
                            />
                          </div>
                          <FieldWrapper style={{ marginBottom: 16 }}>
                            <Label>Week description</Label>
                            <Field
                              as='textarea'
                              name={`sections[${sectionIndex}].description`}
                              rows={3}
                              placeholder='What this week is for. Shown with the week on the plan.'
                              style={{
                                width: '100%',
                                padding: 8,
                                fontSize: 14,
                                border: '1px solid #ccc',
                                borderRadius: 8
                              }}
                            />
                            {errors?.sections?.[sectionIndex]?.description &&
                              touched?.sections?.[sectionIndex]?.description && (
                                <div className='text-danger'>
                                  {errors.sections[sectionIndex].description}
                                </div>
                              )}
                          </FieldWrapper>
                          <div className='section'>
                            {week.items.map((item, itemIndex) => (
                              <CoachingDayItem
                                key={itemIndex}
                                item={item}
                                itemIndex={itemIndex}
                                sectionIndex={sectionIndex}
                                itemList={
                                  classMap[`plan_${item.series}`] || []
                                }
                                seriesItems={seriesItems}
                                getClassListbyPlan={getClassListbyPlan}
                                setFieldValue={setFieldValue}
                                errors={errors}
                              />
                            ))}
                          </div>
                          <CadenceMatrix
                            weekLabel={
                              week.title?.trim() || `Week ${sectionIndex + 1}`
                            }
                            classLabels={week.items.map((item) => {
                              const options = classMap[`plan_${item.series}`] || []
                              return (
                                options.find(
                                  (option) =>
                                    String(option.id) === String(item.id)
                                )?.title || ''
                              )
                            })}
                            slotFilled={week.items.map((item) => Boolean(item.id))}
                            cadenceMaps={week.cadenceMaps || defaultCadenceMaps}
                            fieldPrefix={`sections[${sectionIndex}].cadenceMaps`}
                            setFieldValue={setFieldValue}
                          />
                        </Collapse>
                      </SectionWrapper>
                    )
                  })}
                  <BaseButton
                    className='add-section-btn'
                    onClick={() => {
                      push(emptyWeek(values.sections.length))
                      setOpenSections((state) => ({
                        ...state,
                        [values.sections.length]: true
                      }))
                    }}
                  >
                    + Add week
                  </BaseButton>
                </>
              )}
            </FieldArray>
            {serverError && (
              <div className='text-danger' style={{ marginTop: 12 }}>
                {serverError}
              </div>
            )}
            <SubmitButtonComponent
              isSubmitting={isSubmitting}
              success={success}
              errors={errors}
              touched={touched}
            />
          </Form>
        )}
      </Formik>
    </Main>
  )
}

export default PersonalPlanStructure
